"use client"
import type React from "react"
import { useState } from "react"
import { useDebounceCallback } from "usehooks-ts"
import { classNames } from "../../utils"
import { Tooltip } from "../Tooltip"
import { UnstyledButton, type UnstyledButtonProps } from "../UnstyledButton"

export type CopyToClipboardProps = {
  children: React.ReactNode
  text: string
  tooltipText?: string
  tooltipOnCopiedText?: string
  className?: string
  fullWidth?: boolean
} & UnstyledButtonProps

export function CopyToClipboard({
  children,
  onClick,
  className,
  text,
  tooltipText,
  tooltipOnCopiedText,
  fullWidth = false,
  ...rest
}: CopyToClipboardProps) {
  const [open, setOpen] = useState(false)

  const [hasCopied, setHasCopied] = useState(false)

  const debouncedHasCopied = useDebounceCallback(setHasCopied, 3000)

  const beforeCopiedText = tooltipText ?? "Copy"
  const afterCopiedText = tooltipOnCopiedText ?? "Copied!"

  return (
    <Tooltip
      content={hasCopied ? afterCopiedText : beforeCopiedText}
      hoverable={false}
      onOpenChange={open => {
        if (hasCopied && !open) {
          setHasCopied(false)
        }

        setOpen(open)
      }}
      open={hasCopied ? true : open}
    >
      <UnstyledButton
        className={classNames(fullWidth && "w-full", className)}
        onClick={e => {
          navigator.clipboard.writeText(text)
          debouncedHasCopied.cancel()
          setHasCopied(true)
          debouncedHasCopied(false)
          onClick?.(e)
        }}
        {...rest}
      >
        {children}
      </UnstyledButton>
    </Tooltip>
  )
}
